/**
 * Poll Action Resolver
 *
 * Server-side resolution logic for poll actions.
 * Calculates vote shares based on cohort utilities, adds statistical noise,
 * and generates polling snapshots for player intelligence.
 *
 * All functions are pure and deterministic for testability.
 */

import { IDEOLOGY_AXES } from '@/constants/ideology'
import type {
  Cohort,
  Party,
  CohortPartyLoyalty,
  PartyDistrictState,
  Candidate,
  PartyAction,
  PollingSnapshot,
  PartyStanding,
  CohortBreakdown,
  TopIssue,
  SwingCohort,
  BasicPollResults,
  DetailedPollResults,
  PollType,
  DetailLevel,
} from '@/types'
import { hashStringToNumber, roundToOneDecimal, describeCohort } from './utils'
import { calculateVoteShares, calculateCohortUtilities } from './electionUtils'

// ============================================================================
// Types
// ============================================================================

export interface PollResolutionInput {
  /** The poll action being resolved */
  action: PartyAction
  /** The party commissioning the poll */
  party: Party
  /** All parties in the nation (for standings) */
  allParties: Party[]
  /** Cohorts in target area (all for national, filtered for regional/district) */
  targetCohorts: Cohort[]
  /** All loyalty records for these cohorts */
  cohortLoyalties: CohortPartyLoyalty[]
  /** Recognition data for parties in target districts */
  partyDistrictStates: PartyDistrictState[]
  /** All candidates (for regional candidate quality factor) */
  candidates: Candidate[]
  /** The nation ID for the polling snapshot */
  nationId: string
}

export interface PollResolutionResult {
  /** The polling snapshot to store in the database */
  snapshot: Omit<PollingSnapshot, 'id' | 'created_at'>
  /** Human-readable summary for player feedback */
  summary: string
}

// ============================================================================
// Configuration Constants
// ============================================================================

/**
 * @tuning Sample sizes by poll scope and detail level
 * Larger samples = less noise, but would cost more in real polling
 */
const SAMPLE_SIZES: Record<PollType, Record<DetailLevel, number>> = {
  national: { basic: 800, detailed: 1500 },
  regional: { basic: 500, detailed: 1000 },
  district: { basic: 300, detailed: 600 },
}

/**
 * @tuning Methodology improvement factor for detailed polls
 * Detailed polls use better methodology, reducing effective margin of error
 */
const DETAILED_METHODOLOGY_FACTOR = 0.7

/**
 * @tuning Minimum and maximum margin of error bounds
 * Keeps polls feeling realistic (real polls are typically 2-8%)
 */
const MIN_MARGIN_OF_ERROR = 2
const MAX_MARGIN_OF_ERROR = 10

/**
 * @tuning Number of cohort breakdowns to include in detailed polls
 */
const COHORT_BREAKDOWN_LIMIT = 5

/**
 * @tuning Number of top issues to include in detailed polls
 */
const TOP_ISSUES_LIMIT = 3

/**
 * @tuning Number of swing cohorts to include in detailed polls
 */
const SWING_COHORTS_LIMIT = 3

/**
 * @tuning Threshold for considering a cohort "swing"
 * If top two parties are within this utility difference, it's a swing cohort
 */
const SWING_COHORT_THRESHOLD = 15

// ============================================================================
// Sample Size and Margin of Error
// ============================================================================

/**
 * Determine sample size based on poll scope and detail level.
 */
function determineSampleSize(scope: PollType, detailLevel: DetailLevel): number {
  return SAMPLE_SIZES[scope][detailLevel]
}

/**
 * Calculate margin of error based on sample size and detail level.
 * Uses standard polling formula with methodology adjustment.
 *
 * Formula: MOE = z * sqrt(p*(1-p)/n) * 100
 * Where z = 1.96 for 95% confidence, p = 0.5 (max variance)
 *
 * @param sampleSize - Number of respondents
 * @param detailLevel - Basic or detailed (affects methodology quality)
 * @returns Margin of error as percentage (e.g., 3.5 means ±3.5%)
 */
function calculateMarginOfError(
  sampleSize: number,
  detailLevel: DetailLevel
): number {
  // Base margin of error from sample size (95% confidence interval)
  const baseMargin = 1.96 * Math.sqrt(0.25 / sampleSize) * 100

  // Detailed polls have better methodology
  const methodologyFactor =
    detailLevel === 'detailed' ? DETAILED_METHODOLOGY_FACTOR : 1.0

  // Apply factor and clamp to reasonable range
  const margin = baseMargin * methodologyFactor
  return roundToOneDecimal(
    Math.max(MIN_MARGIN_OF_ERROR, Math.min(MAX_MARGIN_OF_ERROR, margin))
  )
}

// ============================================================================
// Polling Noise
// ============================================================================

/**
 * Add deterministic noise to a vote share to simulate polling error.
 * Uses the action ID as a seed for reproducibility.
 *
 * @param trueShare - The true vote share percentage
 * @param sampleSize - Sample size (affects noise magnitude)
 * @param actionId - Action ID for deterministic seeding
 * @param partyIndex - Party index for unique seed per party
 * @returns Noisy vote share (clamped to 0-100)
 */
function addPollingNoise(
  trueShare: number,
  sampleSize: number,
  actionId: string,
  partyIndex: number
): number {
  // Deterministic seed from action ID and party index
  const seed = hashStringToNumber(`${actionId}-poll-${partyIndex}`)

  // Standard error decreases with sqrt of sample size
  // For a proportion p, SE = sqrt(p * (1-p) / n)
  // We use the true share as p
  const p = trueShare / 100
  const standardError = Math.sqrt((p * (1 - p)) / sampleSize) * 100

  // Generate noise within ~2 standard errors (95% confidence interval)
  const noiseRange = standardError * 2

  // Convert seed to a value between -1 and 1
  const normalizedSeed = ((seed % 1000) / 500 - 1)

  // Apply noise
  const noise = normalizedSeed * noiseRange
  const noisyShare = trueShare + noise

  // Clamp to valid percentage range
  return Math.max(0, Math.min(100, noisyShare))
}

// ============================================================================
// Detailed Poll Helpers
// ============================================================================

/**
 * Generate cohort breakdown showing which party leads in each demographic.
 * Returns the most interesting/diverse cohorts for player intelligence.
 */
function generateCohortBreakdown(
  cohorts: Cohort[],
  parties: Party[],
  loyalties: CohortPartyLoyalty[],
  districtStates: PartyDistrictState[],
  candidates: Candidate[],
  limit: number = COHORT_BREAKDOWN_LIMIT
): CohortBreakdown[] {
  const breakdowns: CohortBreakdown[] = []

  for (const cohort of cohorts) {
    const utilities = calculateCohortUtilities(
      cohort,
      parties,
      loyalties,
      districtStates,
      candidates
    )

    if (utilities.length < 2) continue

    const leader = utilities[0]
    const runnerUp = utilities[1]
    const margin = leader.utility - runnerUp.utility

    breakdowns.push({
      cohort_description: describeCohort(cohort),
      leading_party_id: leader.partyId,
      leading_party_name: leader.partyName,
      margin: roundToOneDecimal(margin),
    })
  }

  // Sort by margin (show most competitive first, then most decisive)
  // This gives players the most interesting information
  breakdowns.sort((a, b) => {
    // Prioritize close races (low margin) for strategic value
    if (a.margin < 10 && b.margin >= 10) return -1
    if (b.margin < 10 && a.margin >= 10) return 1
    // Then sort by margin descending (show strongholds)
    return b.margin - a.margin
  })

  return breakdowns.slice(0, limit)
}

/**
 * Generate top issues by analyzing salience weights across cohorts.
 * Shows which ideology axes matter most to voters in the poll area.
 */
function generateTopIssues(
  cohorts: Cohort[],
  limit: number = TOP_ISSUES_LIMIT
): TopIssue[] {
  // Aggregate salience weights weighted by cohort population
  const aggregateSalience = [0, 0, 0, 0, 0, 0, 0]
  const aggregatePosition = [0, 0, 0, 0, 0, 0, 0]
  let totalPopulation = 0

  for (const cohort of cohorts) {
    for (let i = 0; i < 7; i++) {
      aggregateSalience[i] += cohort.salience_weights[i] * cohort.population
      aggregatePosition[i] += cohort.ideology_vector[i] * cohort.population
    }
    totalPopulation += cohort.population
  }

  // Normalize and create issue list
  const issues: TopIssue[] = IDEOLOGY_AXES.map((axis, index) => {
    const importance =
      totalPopulation > 0 ? aggregateSalience[index] / totalPopulation : 0
    const avgPosition =
      totalPopulation > 0 ? aggregatePosition[index] / totalPopulation : 0

    // Determine which direction voters favor
    let favoredDirection: string
    if (avgPosition < -20) {
      favoredDirection = axis.leftLabel
    } else if (avgPosition > 20) {
      favoredDirection = axis.rightLabel
    } else {
      favoredDirection = 'Mixed'
    }

    return {
      axis_index: axis.index,
      axis_name: axis.label,
      importance: roundToOneDecimal(importance),
      favored_direction: favoredDirection,
    }
  })

  // Sort by importance and take top N
  issues.sort((a, b) => b.importance - a.importance)
  return issues.slice(0, limit)
}

/**
 * Identify swing cohorts where multiple parties are competitive.
 * These are cohorts that could go either way and are valuable targets.
 */
function generateSwingCohorts(
  cohorts: Cohort[],
  parties: Party[],
  loyalties: CohortPartyLoyalty[],
  districtStates: PartyDistrictState[],
  candidates: Candidate[],
  limit: number = SWING_COHORTS_LIMIT
): SwingCohort[] {
  const swingCohorts: Array<{ cohort: Cohort; volatility: number }> = []

  for (const cohort of cohorts) {
    const utilities = calculateCohortUtilities(
      cohort,
      parties,
      loyalties,
      districtStates,
      candidates
    )

    if (utilities.length < 2) continue

    // Volatility = how close the top parties are
    // High volatility = small gap between parties = could go either way
    const gap = utilities[0].utility - utilities[1].utility

    // Only include if it's actually competitive
    if (gap <= SWING_COHORT_THRESHOLD) {
      // Volatility score: inverse of gap, scaled so smaller gaps = higher volatility
      const volatility = roundToOneDecimal(SWING_COHORT_THRESHOLD - gap)

      swingCohorts.push({
        cohort,
        volatility,
      })
    }
  }

  // Sort by volatility (most swing-y first)
  swingCohorts.sort((a, b) => b.volatility - a.volatility)

  return swingCohorts.slice(0, limit).map(({ cohort, volatility }) => ({
    cohort_description: describeCohort(cohort),
    volatility,
  }))
}

// ============================================================================
// Main Resolution Function
// ============================================================================

/**
 * Resolve a poll action and generate results.
 *
 * Process:
 * 1. Calculate true vote shares based on cohort utilities
 * 2. Determine sample size and margin of error
 * 3. Add statistical noise to simulate polling uncertainty
 * 4. For detailed polls: generate cohort breakdowns, top issues, swing cohorts
 * 5. Create polling snapshot record
 * 6. Generate human-readable summary
 *
 * @param input - All data needed for poll resolution
 * @returns Polling snapshot and summary for player feedback
 */
export function resolvePollAction(input: PollResolutionInput): PollResolutionResult {
  const {
    action,
    party,
    allParties,
    targetCohorts,
    cohortLoyalties,
    partyDistrictStates,
    candidates,
    nationId,
  } = input

  // Extract poll parameters
  const parameters = action.parameters as {
    scope: PollType
    detail_level: DetailLevel
    target_name?: string
  }
  const { scope, detail_level, target_name } = parameters

  // Step 1: Calculate true vote shares
  const { voteShares } = calculateVoteShares(
    targetCohorts,
    allParties,
    cohortLoyalties,
    partyDistrictStates,
    candidates
  )

  // Step 2: Determine sample size and margin of error
  const sampleSize = determineSampleSize(scope, detail_level)
  const marginOfError = calculateMarginOfError(sampleSize, detail_level)

  // Step 3: Build party standings with noise
  const partyStandings: PartyStanding[] = allParties
    .map((p, index) => {
      const trueShare = voteShares.get(p.id) || 0
      const reportedShare = addPollingNoise(trueShare, sampleSize, action.id, index)
      const roundedShare = roundToOneDecimal(reportedShare)

      return {
        party_id: p.id,
        party_name: p.name,
        party_color: p.color,
        vote_share: roundedShare,
        vote_share_low: roundToOneDecimal(
          Math.max(0, roundedShare - marginOfError)
        ),
        vote_share_high: roundToOneDecimal(
          Math.min(100, roundedShare + marginOfError)
        ),
      }
    })
    .sort((a, b) => b.vote_share - a.vote_share)

  // Step 4: Build results object (basic or detailed)
  const baseResults: BasicPollResults = {
    party_standings: partyStandings,
    total_respondents: sampleSize,
    methodology_note: `Random sample of ${sampleSize.toLocaleString()} likely voters`,
  }

  let results: BasicPollResults | DetailedPollResults

  if (detail_level === 'detailed') {
    const detailedResults: DetailedPollResults = {
      ...baseResults,
      cohort_breakdown: generateCohortBreakdown(
        targetCohorts,
        allParties,
        cohortLoyalties,
        partyDistrictStates,
        candidates
      ),
      top_issues: generateTopIssues(targetCohorts),
      swing_cohorts: generateSwingCohorts(
        targetCohorts,
        allParties,
        cohortLoyalties,
        partyDistrictStates,
        candidates
      ),
    }
    results = detailedResults
  } else {
    results = baseResults
  }

  // Step 5: Create snapshot record
  const snapshot: Omit<PollingSnapshot, 'id' | 'created_at'> = {
    nation_id: nationId,
    party_id: party.id,
    tick: action.tick,
    poll_type: scope,
    target_id: action.target_id || null,
    results,
    sample_size: sampleSize,
    margin_of_error: marginOfError,
    detail_level,
  }

  // Step 6: Generate summary
  const leader = partyStandings[0]
  const scopeLabel = scope.charAt(0).toUpperCase() + scope.slice(1)
  const targetLabel = target_name ? ` of ${target_name}` : ''
  const detailLabel = detail_level === 'detailed' ? 'Detailed ' : ''

  const summary =
    `${detailLabel}${scopeLabel} poll${targetLabel}: ` +
    `${leader.party_name} leads with ${leader.vote_share}% (±${marginOfError}%). ` +
    `Sample: ${sampleSize.toLocaleString()} voters.`

  return { snapshot, summary }
}
